/**
 * tab configuration for metadoc ai results view
 * defines tab metadata and structure
 */
import type { Tab } from "components/Tabs/Tabs";
import type { Metadoc } from "services/metadoc-api";
import type { DocumentGroup } from "services/document-group-api";
import type { DocumentGroupTag } from "services/document-group-tag-api";
import type { BackgroundJobDataType } from "types/DataTypes";
import type { TabId } from "../metadoc.types";

import MetaDocErrorBoundary from "../workspace/MetaDocErrorBoundary";
import MetaDocViewerWorkspace from "../workspace/MetaDocViewerWorkspace";

type TabConfig = {
  id: TabId;
  icon: string;
  label: string;
};

const TAB_CONFIGS: TabConfig[] = [
  { id: "overview", icon: "dashboard", label: "Overview" },
  { id: "executive-summary", icon: "summarize", label: "Executive Summary" },
  { id: "current-status", icon: "info", label: "Current Status" },
  { id: "per-record", icon: "description", label: "Per Record" },
  { id: "codes", icon: "code", label: "Codes" },
  { id: "medications", icon: "medication", label: "Medications" },
  { id: "not-medicare-covered", icon: "block", label: "Not Medicare Covered" },
  { id: "other-summary", icon: "warning", label: "Other Summary" },
];

type CreateTabsArgs = {
  metadoc: Metadoc;
  activeGroups: DocumentGroup[];
  documentGroupTags: DocumentGroupTag[];
  activePageStats: {
    coveredPages: number;
    totalPages: number;
    percentage: number;
  };
  onRunAIModel: () => void;
  isRunningAIModel: boolean;
  isRunAiModelDisabled: boolean;
  runAiModelDisabledReason?: string;
  lastCompletedAIJob: BackgroundJobDataType | null;
  isRunAiModelReady: boolean;
  isMetadocFetching: boolean;
  groupingSource: "OPS" | "AI";
  resolvedDownloadUrl: string;
  onGroupingSourceChange?: (source: "OPS" | "AI") => void;
  canShowGroupingToggle: boolean;
  onDeletionStateChange: (isDeleting: boolean) => void;
};

export const createTabs = ({
  metadoc,
  activeGroups,
  documentGroupTags,
  activePageStats,
  onRunAIModel,
  isRunningAIModel,
  isRunAiModelDisabled,
  runAiModelDisabledReason,
  lastCompletedAIJob,
  isRunAiModelReady,
  isMetadocFetching,
  groupingSource,
  resolvedDownloadUrl,
  onGroupingSourceChange,
  canShowGroupingToggle,
  onDeletionStateChange,
}: CreateTabsArgs): Tab[] => {
  return TAB_CONFIGS.map((config) => ({
    id: config.id,
    label: (
      <>
        <span className="material-icons">{config.icon}</span>
        {config.label}
      </>
    ),
    content: (
      <MetaDocErrorBoundary
        viewState="ai-results"
        metadocId={metadoc.id}
        tabId={config.id}
      >
        <MetaDocViewerWorkspace
          key={metadoc.id}
          metadoc={metadoc}
          onDeletionStateChange={onDeletionStateChange}
          tabId={config.id}
          documentGroups={activeGroups}
          documentGroupTags={documentGroupTags}
          pageClassificationStats={activePageStats}
          onRunAIModel={onRunAIModel}
          isRunningAIModel={isRunningAIModel}
          isRunAiModelDisabled={isRunAiModelDisabled}
          runAiModelDisabledReason={runAiModelDisabledReason}
          lastCompletedAIJob={lastCompletedAIJob}
          isRunAiModelReady={isRunAiModelReady}
          isMetadocFetching={isMetadocFetching}
          groupingSource={groupingSource}
          resolvedDownloadUrl={resolvedDownloadUrl}
          onGroupingSourceChange={onGroupingSourceChange}
          canShowGroupingToggle={canShowGroupingToggle}
        />
      </MetaDocErrorBoundary>
    ),
  }));
};
